import { useEffect, useState } from "react";
import {
  FaSuitcase,
  FaCalendarAlt,
  FaMoneyBill,
  FaTasks,
  FaUserTie,
  FaClock,
} from "react-icons/fa";
import { CloseButton, StatusMessage } from "../../style/view.styles";
import { fetchEntityData, saveEntityData } from "../../utils/RequestHandler";
import { getLoggedInUser } from "../../utils/AuthHandler";
import LoadingSpinner from "./LoadingSpinner";
import {
  FormContainer,
  StyledForm,
  StyledFormField,
  StyledInput,
  StyledLabel,
  StyledSelect,
  StyledTextarea,
  Subtitle,
  Title,
} from "../../style/modal.form.style";
import FullButton from "../Buttons/FullButton";

const AddOpportunityForm = ({ handleModalClose, onSuccess }) => {
  const [formStatus, setFormStatus] = useState({ message: "", type: "" });
  const [submitting, setSubmitting] = useState(false);
  const [organizationId, setOrganizationId] = useState(null);
  const [employmentTypes, setEmploymentTypes] = useState([]);
  const [workModes, setWorkModes] = useState([]);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    type: "",
    location: "",
    compensation: "",
    skills_required: "",
    required_years_of_experience: "",
    application_deadline: "",
    organization: "",
    employment_type: "",
    work_mode: "",
  });

  useEffect(() => {
    const fetchEmploymentTypes = async () => {
      const response = await fetchEntityData("opportunity_type");
      if (response.success) setEmploymentTypes(response.result);
    };
    const fetchWorkModes = async () => {
      const response = await fetchEntityData("program_delivery_types");
      if (response.success) setWorkModes(response.result);
    };
    const setOrganization = async () => {
      try {
        const user = getLoggedInUser();
        const response = await fetchEntityData("organizations");
        const data = response.result.find(
          (record) => record.added_by === user.username
        );
        setOrganizationId(data.id);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchEmploymentTypes();
    fetchWorkModes();
    setOrganization();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const requestData = {
        ...formData,
        organization: organizationId,
        posted_date: new Date(),
      };
      const response = await saveEntityData("opportunities", requestData, true);
      if (response.success) {
        setFormStatus({
          message: "Opportunity added successfully!",
          type: "success",
        });
        if (onSuccess) onSuccess();
      } else {
        setFormStatus({
          message: response.message ?? "An error occurred. Please try again.",
          type: "error",
        });
      }
    } catch (error) {
      console.error(error);
      setFormStatus({
        message: "An error occurred while adding your opportunity.",
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormContainer>
      <CloseButton onClick={handleModalClose}>X</CloseButton>
      <Title>Add a New Opportunity</Title>
      <Subtitle>
        Fill in the form below to post a new job or opportunity for applicants
        to apply.
      </Subtitle>

      <StyledForm>
        {loading ? (
          <LoadingSpinner />
        ) : (
          <>
            <StyledFormField>
              <StyledLabel>
                <FaSuitcase style={{ marginRight: 5 }} /> Title
              </StyledLabel>
              <StyledInput
                type="text"
                name="title"
                placeholder="e.g. Frontend Developer"
                value={formData.title}
                onChange={handleInputChange}
                required
              />
            </StyledFormField>

            <StyledFormField>
              <StyledLabel>
                <FaUserTie style={{ marginRight: 5 }} /> Employment Type
              </StyledLabel>
              <StyledSelect
                value={formData.employment_type ?? ""}
                onChange={(e) =>
                  setFormData({ ...formData, employment_type: e.target.value })
                }
              >
                <option value="">Select type</option>
                {employmentTypes.map((option) => (
                  <option key={option.type} value={option.id}>
                    {option.type}
                  </option>
                ))}
              </StyledSelect>
            </StyledFormField>

            <StyledFormField fullWidth>
              <StyledLabel>
                <FaTasks style={{ marginRight: 5 }} /> Description
              </StyledLabel>
              <StyledTextarea
                name="description"
                placeholder="Brief job description"
                rows="4"
                value={formData.description}
                onChange={handleInputChange}
                required
              />
            </StyledFormField>

            <StyledFormField>
              <StyledLabel>
                <FaClock style={{ marginRight: 5 }} /> Work Mode
              </StyledLabel>
              <StyledSelect
                value={formData.work_mode ?? ""}
                onChange={(e) =>
                  setFormData({ ...formData, work_mode: e.target.value })
                }
              >
                <option value="">Select mode</option>
                {workModes.map((option) => (
                  <option key={option.type} value={option.id}>
                    {option.type}
                  </option>
                ))}
              </StyledSelect>
            </StyledFormField>

            <StyledFormField>
              <StyledLabel>
                <FaTasks style={{ marginRight: 5 }} /> Skills Required
              </StyledLabel>
              <StyledInput
                type="text"
                name="skills_required"
                placeholder="e.g. React, TypeScript"
                value={formData.skills_required}
                onChange={handleInputChange}
                required
              />
            </StyledFormField>

            <StyledFormField>
              <StyledLabel>
                <FaUserTie style={{ marginRight: 5 }} /> Years of Experience
              </StyledLabel>
              <StyledInput
                type="number"
                name="required_years_of_experience"
                placeholder="e.g. 2"
                value={formData.required_years_of_experience}
                onChange={handleInputChange}
                required
              />
            </StyledFormField>

            <StyledFormField>
              <StyledLabel>
                <FaMoneyBill style={{ marginRight: 5 }} /> Compensation
              </StyledLabel>
              <StyledInput
                type="text"
                name="compensation"
                placeholder="e.g. $60,000/year"
                value={formData.compensation}
                onChange={handleInputChange}
                required
              />
            </StyledFormField>

            <StyledFormField>
              <StyledLabel>
                <FaCalendarAlt style={{ marginRight: 5 }} /> Application
                Deadline
              </StyledLabel>
              <StyledInput
                type="date"
                name="application_deadline"
                value={formData.application_deadline}
                onChange={handleInputChange}
                required
              />
            </StyledFormField>
          </>
        )}
      </StyledForm>
      <FullButton action={handleSubmit} type="submit" disabled={submitting} title={submitting ? "Saving..." : "Save Opportunity"}/>
      {formStatus.message && (
        <StatusMessage type={formStatus.type}>
          {formStatus.message}
        </StatusMessage>
      )}
    </FormContainer>
  );
};

export default AddOpportunityForm;
